import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { usePortalParticipants } from "../../context/PortalParticipantsContext";
import { usePortalChrome } from "../../context/PortalChromeContext";
import { useVisualViewportBottom } from "../../hooks/useVisualViewportBottom";
import { toast } from "sonner";
import { Send, Plus, ChevronDown, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { AppAvatar } from "../../components/AppAvatar";
import { messageAuthorAvatarSrc } from "../../lib/portalAvatars";
import { cn } from "../../lib/utils";

/** Sent bubble — same fill as calendar milestone bars (CalendarPage BAR_COLORS.milestone). */
const MSG_SENT_BAR = "bg-[rgba(0,30,255,0.12)] text-[var(--t-text)] shadow-none";
/** Incoming bubble — reads on portal background. */
const MSG_RECV_BAR =
  "bg-[var(--neo-surface,rgba(252,252,252,0.78))] text-[var(--t-text)] border border-white/65 shadow-none";
/** Conversation areas use portal background (transparent over main). */
const CHAT_BG = "bg-transparent";
/** Frost matches topic `PopoverContent` (`.t-glass` in index.css). */
const COMPOSER_GLASS = "t-glass";
const COMPOSER_SEND_ACTIVE_TINT =
  "!bg-[rgba(0,30,255,0.1)] !border-[rgba(0,30,255,0.28)]";

function messageComposerBarClass(composerActive) {
  return cn(
    "pointer-events-none z-20 px-3 sm:px-6 lg:px-3 pt-2",
    "bg-[rgba(252,252,252,0.28)] backdrop-blur-[14px] [-webkit-backdrop-filter:blur(14px)]",
    "max-lg:fixed max-lg:inset-x-0",
    composerActive
      ? "max-lg:bottom-[var(--vv-bottom-offset,0px)] max-lg:pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      : "max-lg:bottom-0 max-lg:pb-2",
    "lg:absolute lg:inset-x-0 lg:bottom-0 lg:pb-0",
  );
}

function localDayKey(iso) {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

function formatDateDividerLabel(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (localDayKey(iso) === localDayKey(now.toISOString())) return "Today";
  const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (localDayKey(iso) === localDayKey(yest.toISOString())) return "Yesterday";
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

/** Left-column bubbles: show uploaded client icon when the sender is the portal client. */
function incomingAvatarSrc(message, portalClient, user, participants) {
  return messageAuthorAvatarSrc(message, { participants, user, client: portalClient });
}

function outgoingAvatarSrc(message, portalClient, user, participants) {
  return messageAuthorAvatarSrc(message, { participants, user, client: portalClient });
}

function senderKey(m) {
  return `${m.author_role}|${m.author_name}`;
}

function buildTimeline(messages) {
  const rows = [];
  let prevDayKey = null;
  let prevSender = null;
  let prevRowType = null;

  (messages || []).forEach((m) => {
    const dayKey = localDayKey(m.created_at);
    if (dayKey !== prevDayKey) {
      rows.push({
        type: "date",
        key: `date-${dayKey}-${m.id}`,
        label: formatDateDividerLabel(m.created_at),
      });
      prevDayKey = dayKey;
      prevSender = null;
      prevRowType = "date";
    }

    const sk = senderKey(m);
    const isNewSenderGroup = sk !== prevSender;
    prevSender = sk;

    let marginTopClass = "";
    if (prevRowType === "date") marginTopClass = "mt-3";
    else if (prevRowType === "msg") {
      marginTopClass = isNewSenderGroup ? "mt-6" : "mt-2";
    }

    rows.push({
      type: "msg",
      key: m.id,
      m,
      isNewSenderGroup,
      marginTopClass,
    });
    prevRowType = "msg";
  });

  return rows;
}

function formatMessageTimestamp(iso) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const MESSAGE_DELETE_MS = 2 * 60 * 1000;

function messageWithinDeleteWindow(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= MESSAGE_DELETE_MS;
}

const PICKER_EMOJIS = ["👍", "🤍", "☺️", "🪩", "🥳", "🌞", "🤓"];
const LONG_PRESS_MS = 420;

function aggregateReactions(reactions, userId) {
  const map = new Map();
  for (const r of reactions || []) {
    const e = r?.emoji;
    if (!e) continue;
    if (!map.has(e)) map.set(e, { emoji: e, count: 0, me: false });
    const g = map.get(e);
    g.count += 1;
    if (r.user_id === userId) g.me = true;
  }
  return [...map.values()].sort((a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji));
}

function useMessageLongPress(onOpen) {
  const timerRef = useRef(null);
  const startPosRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const openFromTarget = useCallback((target) => {
    longPressTriggeredRef.current = true;
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(12);
    onOpen(target.getBoundingClientRect());
  }, [onOpen]);

  const onTouchStart = useCallback((e) => {
    longPressTriggeredRef.current = false;
    const touch = e.touches[0];
    if (!touch) return;
    startPosRef.current = { x: touch.clientX, y: touch.clientY };
    const target = e.currentTarget;
    cancel();
    timerRef.current = setTimeout(() => openFromTarget(target), LONG_PRESS_MS);
  }, [cancel, openFromTarget]);

  const onTouchMove = useCallback((e) => {
    const start = startPosRef.current;
    if (!start) return;
    const touch = e.touches[0];
    if (!touch) return;
    if (Math.abs(touch.clientX - start.x) > 10 || Math.abs(touch.clientY - start.y) > 10) cancel();
  }, [cancel]);

  const onTouchEnd = useCallback(() => {
    cancel();
    startPosRef.current = null;
  }, [cancel]);

  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    longPressTriggeredRef.current = false;
    const target = e.currentTarget;
    cancel();
    timerRef.current = setTimeout(() => openFromTarget(target), LONG_PRESS_MS);
  }, [cancel, openFromTarget]);

  const onMouseUp = useCallback(() => cancel(), [cancel]);
  const onMouseLeave = useCallback(() => cancel(), [cancel]);

  const onContextMenu = useCallback((e) => {
    e.preventDefault();
    if (!longPressTriggeredRef.current) openFromTarget(e.currentTarget);
  }, [openFromTarget]);

  useEffect(() => () => cancel(), [cancel]);

  return {
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMouseDown,
      onMouseUp,
      onMouseLeave,
      onContextMenu,
    },
  };
}

function MessageBubble({ messageId, className, reactionActive, onOpenReactionPicker, children }) {
  const { handlers } = useMessageLongPress(onOpenReactionPicker);

  return (
    <div
      className={cn(
        className,
        "touch-manipulation select-none transition-transform duration-150",
        reactionActive && "scale-[0.98] opacity-90",
      )}
      data-testid={`message-bubble-${messageId}`}
      {...handlers}
    >
      {children}
    </div>
  );
}

const TAPBACK_SURFACE = "bg-[#E8E8ED] shadow-[0_1px_4px_rgba(0,0,0,0.14)]";

function TapbackStem({ alignEnd }) {
  return (
    <div
      className={cn(
        "flex flex-col gap-px",
        alignEnd ? "items-start ml-2.5" : "items-end mr-2.5",
      )}
      aria-hidden
    >
      <span className={cn("block h-[4px] w-[4px] rounded-full", TAPBACK_SURFACE)} />
      <span className={cn("block h-[2.5px] w-[2.5px] rounded-full", TAPBACK_SURFACE)} />
    </div>
  );
}

function MessageReactions({ messageId, reactions, alignEnd, userId, onToggle }) {
  const grouped = useMemo(() => aggregateReactions(reactions, userId), [reactions, userId]);
  if (grouped.length === 0) return null;

  return (
    <div
      className={cn(
        "absolute z-10 pointer-events-none",
        alignEnd
          ? "left-0 top-0 -translate-x-1 -translate-y-[calc(100%-5px)]"
          : "right-0 top-0 translate-x-1 -translate-y-[calc(100%-5px)]",
      )}
      data-testid={`message-reactions-${messageId}`}
    >
      <div className={cn("flex flex-col", alignEnd ? "items-start" : "items-end")}>
        <div className={cn("flex items-center pointer-events-auto", grouped.length > 1 && "-space-x-1.5")}>
          {grouped.map(({ emoji, count, me }) => (
            <button
              key={emoji}
              type="button"
              onClick={() => onToggle(messageId, emoji)}
              className={cn(
                "relative flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full px-1 text-[14px] leading-none ring-2 ring-[var(--t-bg)] transition-transform active:scale-95",
                TAPBACK_SURFACE,
                me && "ring-[rgba(0,30,255,0.35)]",
              )}
              aria-label={me ? `Remove ${emoji} reaction` : `Add ${emoji} reaction`}
            >
              <span className="select-none" aria-hidden>
                {emoji}
              </span>
              {count > 1 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full bg-[var(--t-meta)] px-0.5 text-[8px] font-medium leading-none text-white">
                  {count}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <TapbackStem alignEnd={alignEnd} />
      </div>
    </div>
  );
}

function MessageReactionPicker({ target, onSelect, onClose }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!target?.rect || !panelRef.current) return;
    const panel = panelRef.current.getBoundingClientRect();
    const margin = 12;
    const gap = 8;
    const { rect } = target;

    let left = rect.left + rect.width / 2 - panel.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - panel.width - margin));

    let top = rect.top - panel.height - gap;
    if (top < margin) top = rect.bottom + gap;

    setPos({ top, left });
  }, [target]);

  useEffect(() => {
    if (!target) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [target, onClose]);

  if (!target || typeof document === "undefined") return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70]" aria-hidden onClick={onClose} />
      <div
        ref={panelRef}
        className="fixed z-[71] flex items-center gap-0.5 rounded-full border border-[var(--t-border)] bg-[var(--t-bg)] px-2 py-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.12)] animate-in fade-in-0 zoom-in-95 duration-150"
        style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999, visibility: "hidden" }}
        role="toolbar"
        aria-label="Add reaction"
        data-testid={`message-reaction-picker-${target.messageId}`}
        onClick={(e) => e.stopPropagation()}
      >
        {PICKER_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full text-xl leading-none transition-transform active:scale-125 hover:bg-[rgba(0,30,255,0.08)]"
            onClick={() => onSelect(emoji)}
            aria-label={`React with ${emoji}`}
          >
            <span className="select-none">{emoji}</span>
          </button>
        ))}
      </div>
    </>,
    document.body,
  );
}

export default function MessagesPage() {
  const { client, user } = useAuth();
  const participants = usePortalParticipants();
  const { composerActive, setComposerActive } = usePortalChrome();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState("");
  const [topicOpen, setTopicOpen] = useState(false);
  const [composingNew, setComposingNew] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [bootstrapped, setBootstrapped] = useState(false);
  const params = isAdmin ? `?client_id=${client?.id}` : "";
  const endRef = useRef(null);
  const threadScrollRef = useRef(null);

  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [reactionPicker, setReactionPicker] = useState(null);

  useVisualViewportBottom(composerActive);

  useEffect(() => () => setComposerActive(false), [setComposerActive]);

  const onComposerFocus = useCallback(() => setComposerActive(true), [setComposerActive]);
  const onComposerBlur = useCallback(() => {
    requestAnimationFrame(() => {
      if (document.activeElement?.closest("[data-message-composer]")) return;
      setComposerActive(false);
    });
  }, [setComposerActive]);

  const load = async () => {
    if (!client) return;
    try {
      const { data } = await api.get(`/portal/messages${params}`);
      setThreads(data);
      const threadFromUrl = searchParams.get("thread");
      if (threadFromUrl && data.some((t) => t.thread_id === threadFromUrl)) {
        setActiveId(threadFromUrl);
        setComposingNew(false);
      } else if (!activeId && !composingNew && data.length > 0) {
        setActiveId(data[0].thread_id);
      }
      api.post(`/portal/messages/mark-read${params}`).catch(() => {});
    } finally {
      setBootstrapped(true);
    }
  };

  useEffect(() => {
    setBootstrapped(false);
  }, [client?.id]);

  const active = threads.find((t) => t.thread_id === activeId);

  const timeline = useMemo(() => buildTimeline(active?.messages), [active?.messages]);

  useEffect(() => {
    load();
  }, [client, searchParams]);

  useEffect(() => {
    const el = threadScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
    });
  }, [activeId, threads, timeline.length]);

  const send = async () => {
    if (!text.trim()) return;
    const url = isAdmin ? `/portal/messages?client_id=${client.id}` : `/portal/messages`;
    if (composingNew) {
      if (!newTopic.trim()) {
        toast.error("Name your thread");
        return;
      }
      try {
        const { data } = await api.post(url, { topic: newTopic.trim(), text: text.trim() });
        setText("");
        setNewTopic("");
        setComposingNew(false);
        await load();
        setActiveId(data.thread_id);
      } catch {
        toast.error("Could not start thread");
      }
      return;
    }
    if (!active) return;
    await api.post(url, { thread_id: active.thread_id, topic: active.topic, text: text.trim() });
    setText("");
    load();
  };

  const beginNewThread = () => {
    setActiveId(null);
    setComposingNew(true);
    setNewTopic("");
    setText("");
    setTopicOpen(false);
  };

  const toggleReaction = async (messageId, emoji) => {
    if (!client || !user?.id) return;
    try {
      const url = isAdmin ? `/portal/messages/${messageId}/react?client_id=${client.id}` : `/portal/messages/${messageId}/react`;
      await api.post(url, { emoji });
      await load();
    } catch {
      toast.error("Could not update reaction");
    }
  };

  const openReactionPicker = useCallback((messageId, alignEnd) => (rect) => {
    setReactionPicker({ messageId, alignEnd, rect });
  }, []);

  const closeReactionPicker = useCallback(() => setReactionPicker(null), []);

  const selectReaction = useCallback(async (emoji) => {
    if (!reactionPicker) return;
    const { messageId } = reactionPicker;
    setReactionPicker(null);
    await toggleReaction(messageId, emoji);
  }, [reactionPicker]);

  useEffect(() => {
    if (!reactionPicker) return undefined;
    const el = threadScrollRef.current;
    if (!el) return undefined;
    const close = () => setReactionPicker(null);
    el.addEventListener("scroll", close, { passive: true });
    return () => el.removeEventListener("scroll", close);
  }, [reactionPicker]);

  const confirmDeleteMessage = async () => {
    const messageId = deleteTargetId;
    if (!messageId || !client) return;
    try {
      const url = isAdmin ? `/portal/messages/${messageId}?client_id=${client.id}` : `/portal/messages/${messageId}`;
      await api.delete(url);
      toast.success("Message deleted");
      setDeleteTargetId(null);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not delete message");
    }
  };

  if (!client) return <div className="p-6 sm:p-12 t-meta">No client context.</div>;

  const bubbleRadius = "rounded-[var(--t-event-pill-radius)]";

  const mobileHeightClass = composerActive
    ? "max-lg:h-[calc(100dvh-var(--portal-mobile-header-h,68px))] max-lg:max-h-[calc(100dvh-var(--portal-mobile-header-h,68px))]"
    : "max-lg:h-[calc(100dvh-var(--portal-mobile-header-h,68px))] max-lg:max-h-[calc(100dvh-var(--portal-mobile-header-h,68px))]";

  return (
    <div
      className={`t-fade-up max-w-full min-w-0 flex flex-col min-h-0 flex-1 overflow-hidden ${CHAT_BG} ${mobileHeightClass} lg:h-[calc(100dvh-var(--portal-sidebar-footer-h,0px))] lg:max-h-[calc(100dvh-var(--portal-sidebar-footer-h,0px))]`}
      data-testid="messages-page"
    >
      <div className="flex flex-col flex-1 min-h-0 min-w-0 max-w-full">
        {active ? (
          <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full">
              <header className={`shrink-0 min-w-0 pt-3 sm:pt-6 md:pt-12 ${CHAT_BG}`}>
                <div className="px-3 sm:px-6 md:px-3 py-2.5">
                  <Popover open={topicOpen} onOpenChange={setTopicOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`relative w-full flex items-center justify-center min-h-[40px] max-w-full box-border rounded-[var(--t-event-pill-radius)] !shadow-none pl-4 pr-11 text-[15px] font-light font-[inherit] text-[var(--t-text)] outline-none transition-[border-color] duration-[160ms] ease-out focus-visible:outline-none focus-visible:ring-0 ${COMPOSER_GLASS}`}
                        data-testid="active-thread-topic"
                      >
                        <span className="truncate text-center">{active.topic}</span>
                        <ChevronDown size={17} strokeWidth={1.6} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-45 shrink-0 text-[var(--t-text)] pointer-events-none" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      side="bottom"
                      sideOffset={4}
                      className="z-[60] box-border min-w-0 w-[var(--radix-popper-anchor-width)] overflow-hidden neo-panel p-0 text-[var(--t-text)] t-glass shadow-none data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 data-[side=bottom]:slide-in-from-top-0"
                    >
                      <div className="border-b border-[var(--t-border)] p-3 flex items-center justify-between gap-3">
                        <div className="label-eyebrow">Select topic</div>
                      </div>
                      <div className="max-h-[340px] overflow-auto p-2">
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 min-h-[44px] border border-transparent hover:border-[var(--t-border)]"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            beginNewThread();
                          }}
                          data-testid="topic-option-new-thread"
                        >
                          <div className="font-heading text-sm tracking-tight inline-flex items-center gap-2">
                            <Plus size={14} strokeWidth={1.6} />
                            New thread
                          </div>
                        </button>
                        <div className="my-2 border-t border-[var(--t-border)]" />
                        {threads.length === 0 && <div className="t-meta text-xs px-3 py-4">No conversations yet.</div>}
                        {threads.map((t) => {
                          const selected = t.thread_id === activeId;
                          const last = t.messages?.[t.messages.length - 1];
                          return (
                            <button
                              key={t.thread_id}
                              type="button"
                              className={`w-full text-left px-3 py-2 min-h-[44px] border border-transparent hover:border-[var(--t-border)] ${selected ? "bg-[rgba(0,30,255,0.08)]" : ""}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setComposingNew(false);
                                setActiveId(t.thread_id);
                                setTopicOpen(false);
                              }}
                              data-testid={`topic-option-${t.thread_id}`}
                            >
                              <div className="font-heading text-sm tracking-tight truncate mb-1">{t.topic}</div>
                              {last && (
                                <div className="t-meta text-xs truncate">
                                  {last.author_name}: {last.text}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </header>
              <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full">
                <div className="relative min-h-0 flex-1 min-w-0 w-full overflow-hidden">
                  <div
                    ref={threadScrollRef}
                    className={`absolute inset-0 z-0 overflow-y-auto overscroll-y-contain px-3 sm:px-6 md:px-3 pt-1 pb-[4.25rem] md:pb-[max(4.5rem,calc(3.5rem+env(safe-area-inset-bottom)))] ${CHAT_BG}`}
                    data-testid="thread-messages"
                  >
                  <div className="min-h-full flex flex-col justify-end gap-0">
                  {timeline.map((row) => {
                    if (row.type === "date") {
                      return (
                        <div key={row.key} className="flex items-center gap-3 py-3 shrink-0" data-testid="message-date-divider">
                          <div className="h-px min-h-px flex-1 bg-[var(--t-border)]" aria-hidden />
                          <span className="shrink-0 rounded-[var(--t-event-pill-radius)] bg-[var(--t-bg)] px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[var(--t-meta)]">
                            {row.label}
                          </span>
                          <div className="h-px min-h-px flex-1 bg-[var(--t-border)]" aria-hidden />
                        </div>
                      );
                    }

                    const m = row.m;
                    const own = m.author_role === user?.role;
                    const canDelete = isAdmin || (own && messageWithinDeleteWindow(m.created_at));
                    const showName = row.isNewSenderGroup && !own;

                    return (
                      <div key={row.key} className={`min-w-0 ${row.marginTopClass}`}>
                        {own ? (
                          <div className="flex justify-end">
                            <div className="flex max-w-[min(88%,21rem)] sm:max-w-[70%] min-w-0 gap-2">
                              <div className="min-w-0 flex-1 flex flex-col items-end gap-1">
                              <div className={cn("relative min-w-0 w-full overflow-visible", m.reactions?.length > 0 && "mt-5")}>
                              <MessageBubble
                                messageId={m.id}
                                className={`min-w-0 w-full ${bubbleRadius} px-3.5 py-2.5 ${MSG_SENT_BAR}`}
                                reactionActive={reactionPicker?.messageId === m.id}
                                onOpenReactionPicker={openReactionPicker(m.id, true)}
                              >
                                <p className="text-[15px] leading-snug font-light break-words [overflow-wrap:anywhere]">{m.text}</p>
                              </MessageBubble>
                              <MessageReactions
                                messageId={m.id}
                                reactions={m.reactions}
                                alignEnd
                                userId={user?.id}
                                onToggle={toggleReaction}
                              />
                              </div>
                              <div className="flex w-full items-center justify-end gap-1.5 pr-0.5">
                                <time
                                  className="text-[10px] leading-tight text-[var(--t-meta)] tabular-nums"
                                  dateTime={m.created_at}
                                >
                                  {formatMessageTimestamp(m.created_at)}
                                </time>
                                {canDelete ? (
                                  <button
                                    type="button"
                                    onClick={() => setDeleteTargetId(m.id)}
                                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--t-meta)] transition-colors hover:bg-[rgba(0,30,255,0.08)] hover:text-[var(--t-text)]"
                                    aria-label="Delete message"
                                    data-testid={`message-delete-${m.id}`}
                                  >
                                    <Trash2 size={12} strokeWidth={1.5} />
                                  </button>
                                ) : null}
                              </div>
                              </div>
                              <span className="mt-0.5 inline-flex shrink-0 self-start" aria-hidden>
                                <AppAvatar name={m.author_name} src={outgoingAvatarSrc(m, client, user, participants)} size={36} />
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-start">
                            <div className="flex max-w-[min(88%,23rem)] sm:max-w-[72%] min-w-0 gap-2">
                              <span className="mt-0.5 inline-flex shrink-0 self-start" aria-hidden>
                                <AppAvatar name={m.author_name} src={incomingAvatarSrc(m, client, user, participants)} size={36} />
                              </span>
                              <div className="min-w-0 flex-1 flex flex-col gap-1">
                                {showName && (
                                  <div className="pl-1 text-[11px] leading-tight text-[var(--t-meta)]">{m.author_name}</div>
                                )}
                                <div className={cn("relative min-w-0 overflow-visible", m.reactions?.length > 0 && "mt-5")}>
                                <MessageBubble
                                  messageId={m.id}
                                  className={`min-w-0 ${bubbleRadius} px-3.5 py-2.5 ${MSG_RECV_BAR}`}
                                  reactionActive={reactionPicker?.messageId === m.id}
                                  onOpenReactionPicker={openReactionPicker(m.id, false)}
                                >
                                  <p className="text-[15px] leading-snug font-light break-words [overflow-wrap:anywhere]">{m.text}</p>
                                </MessageBubble>
                                <MessageReactions
                                  messageId={m.id}
                                  reactions={m.reactions}
                                  alignEnd={false}
                                  userId={user?.id}
                                  onToggle={toggleReaction}
                                />
                                </div>
                                <div className="flex w-full items-center gap-1.5 pl-0.5">
                                  <time
                                    className="text-[10px] leading-tight text-[var(--t-meta)] tabular-nums"
                                    dateTime={m.created_at}
                                  >
                                    {formatMessageTimestamp(m.created_at)}
                                  </time>
                                  {canDelete ? (
                                    <button
                                      type="button"
                                      onClick={() => setDeleteTargetId(m.id)}
                                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--t-event-pill-radius)] text-[var(--t-meta)] transition-colors hover:bg-[rgba(0,30,255,0.08)] hover:text-[var(--t-text)]"
                                      aria-label="Delete message"
                                      data-testid={`message-delete-${m.id}`}
                                    >
                                      <Trash2 size={12} strokeWidth={1.5} />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                  </div>
                  </div>
                <div
                  className={messageComposerBarClass(composerActive)}
                  data-message-composer
                >
                  <div className="pointer-events-auto flex flex-row flex-nowrap items-center gap-2 min-w-0">
                    <input
                      className={`min-w-0 flex-1 min-h-[40px] max-w-full w-full box-border rounded-[var(--t-event-pill-radius)] !shadow-none px-4 text-[15px] font-light font-[inherit] text-[var(--t-text)] placeholder:text-[var(--t-meta)] placeholder:font-light outline-none transition-[border-color] duration-[160ms] ease-out focus-visible:outline-none focus-visible:ring-0 ${COMPOSER_GLASS}`}
                      placeholder="Write a reply…"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onFocus={onComposerFocus}
                      onBlur={onComposerBlur}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                      data-testid="message-input"
                    />
                    <button
                      type="button"
                      onClick={send}
                      className={
                        text.trim()
                          ? `inline-flex items-center justify-center !h-10 !w-10 !min-h-10 !min-w-10 shrink-0 !rounded-[var(--t-event-pill-radius)] !shadow-none box-border text-[var(--t-text)] font-[inherit] transition-[background-color,border-color] duration-150 focus-visible:!outline-none focus-visible:!ring-0 disabled:cursor-not-allowed ${COMPOSER_GLASS} ${COMPOSER_SEND_ACTIVE_TINT}`
                          : `inline-flex items-center justify-center !h-10 !w-10 !min-h-10 !min-w-10 shrink-0 !rounded-[var(--t-event-pill-radius)] !shadow-none transition-[background-color,border-color] duration-150 focus-visible:!outline-none focus-visible:!ring-0 disabled:cursor-not-allowed ${COMPOSER_GLASS}`
                      }
                      disabled={!text.trim()}
                      aria-label="Send"
                      data-testid="send-message-btn"
                    >
                      <Send
                        size={17}
                        strokeWidth={1.5}
                        aria-hidden
                        className={`transition-colors duration-150 ${text.trim() ? "text-[var(--t-text)]" : "text-[var(--t-meta)]"}`}
                      />
                    </button>
                  </div>
                </div>
                </div>
              </div>
          </div>
          ) : composingNew ? (
          <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full">
            <header className={`shrink-0 min-w-0 pt-3 sm:pt-6 md:pt-12 ${CHAT_BG}`}>
              <div className="px-3 sm:px-6 md:px-3 py-2.5">
                <input
                  className={`w-full min-h-[40px] max-w-full box-border rounded-[var(--t-event-pill-radius)] !shadow-none px-4 text-[15px] font-light font-[inherit] text-[var(--t-text)] placeholder:text-[var(--t-meta)] placeholder:font-light outline-none transition-[border-color] duration-[160ms] ease-out focus-visible:outline-none focus-visible:ring-0 ${COMPOSER_GLASS}`}
                  placeholder="Thread name"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  data-testid="new-thread-topic-input"
                />
              </div>
            </header>
            <div className="flex flex-col flex-1 min-h-0 min-w-0 w-full">
              <div className="relative min-h-0 flex-1 min-w-0 w-full overflow-hidden">
                <div className={`absolute inset-0 z-0 ${CHAT_BG}`} data-testid="new-thread-compose" />
                <div
                  className={messageComposerBarClass(composerActive)}
                  data-message-composer
                >
                  <div className="pointer-events-auto flex flex-row flex-nowrap items-center gap-2 min-w-0">
                    <input
                      className={`min-w-0 flex-1 min-h-[40px] max-w-full w-full box-border rounded-[var(--t-event-pill-radius)] !shadow-none px-4 text-[15px] font-light font-[inherit] text-[var(--t-text)] placeholder:text-[var(--t-meta)] placeholder:font-light outline-none transition-[border-color] duration-[160ms] ease-out focus-visible:outline-none focus-visible:ring-0 ${COMPOSER_GLASS}`}
                      placeholder="Write your message…"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onFocus={onComposerFocus}
                      onBlur={onComposerBlur}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                      data-testid="message-input"
                    />
                    <button
                      type="button"
                      onClick={send}
                      className={
                        text.trim() && newTopic.trim()
                          ? `inline-flex items-center justify-center !h-10 !w-10 !min-h-10 !min-w-10 shrink-0 !rounded-[var(--t-event-pill-radius)] !shadow-none box-border text-[var(--t-text)] font-[inherit] transition-[background-color,border-color] duration-150 focus-visible:!outline-none focus-visible:!ring-0 disabled:cursor-not-allowed ${COMPOSER_GLASS} ${COMPOSER_SEND_ACTIVE_TINT}`
                          : `inline-flex items-center justify-center !h-10 !w-10 !min-h-10 !min-w-10 shrink-0 !rounded-[var(--t-event-pill-radius)] !shadow-none transition-[background-color,border-color] duration-150 focus-visible:!outline-none focus-visible:!ring-0 disabled:cursor-not-allowed ${COMPOSER_GLASS}`
                      }
                      disabled={!text.trim() || !newTopic.trim()}
                      aria-label="Send"
                      data-testid="send-message-btn"
                    >
                      <Send
                        size={17}
                        strokeWidth={1.5}
                        aria-hidden
                        className={`transition-colors duration-150 ${text.trim() && newTopic.trim() ? "text-[var(--t-text)]" : "text-[var(--t-meta)]"}`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ) : !bootstrapped ? (
            <div className={`flex-1 min-h-0 ${CHAT_BG}`} aria-hidden />
          ) : (
            <div className={`flex-1 flex min-h-0 items-center justify-center px-3 sm:px-6 md:px-3 pt-3 sm:pt-6 md:pt-12 pb-12 text-center ${CHAT_BG}`}>
              <button
                type="button"
                onClick={beginNewThread}
                className="m-0 border-0 bg-transparent p-0 text-[15px] font-light text-[var(--t-accent)] cursor-pointer"
                data-testid="start-thread-btn"
              >
                Start a thread
              </button>
            </div>
          )}
      </div>

      <MessageReactionPicker
        target={reactionPicker}
        onSelect={selectReaction}
        onClose={closeReactionPicker}
      />

      <AlertDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetId(null);
        }}
      >
        <AlertDialogContent
          className="z-[80] max-w-md border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] shadow-none sm:rounded-[var(--t-event-pill-radius)] gap-5"
          data-testid="message-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-light tracking-tight text-[var(--t-text)]">
              Delete this message?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm font-light text-[var(--t-meta)]">
              This removes it from the thread for everyone. You cannot undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 sm:justify-end">
            <AlertDialogCancel className="t-btn-secondary min-h-[44px] border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-text)] hover:opacity-90">
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className="neo-cta"
              data-testid="message-delete-confirm-btn"
              onClick={confirmDeleteMessage}
            >
              Delete
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
